import { Link } from "react-router-dom";
import { ArrowUpRight, Phone, Mail, MapPin, Facebook, Instagram, Youtube } from "lucide-react";
import { STONES } from "@/data/stones";
import { SHOWROOMS } from "@/data/showrooms";
import { Reveal } from "@/components/Motion";

const SOCIALS = [
  { label: "Facebook", href: "https://www.facebook.com/varunmarble/", Icon: Facebook },
  { label: "Instagram", href: "https://www.instagram.com/varunmarblesin/", Icon: Instagram },
  { label: "YouTube", href: "https://www.youtube.com/@varunmarbles", Icon: Youtube },
];

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-primary text-primary-foreground">
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-24 md:pt-32 pb-12">
        <Reveal>
          <Link
            to="/contact"
            data-testid="footer-cta"
            className="group block border-b border-primary-foreground/15 pb-16 md:pb-24"
          >
            <p className="text-xs tracking-[0.25em] uppercase text-primary-foreground/50 mb-6">
              Begin your project
            </p>
            <span className="flex items-end justify-between gap-6">
              <span className="font-serif text-4xl sm:text-5xl lg:text-7xl font-light tracking-tight leading-none">
                Let's build <span className="italic">in stone.</span>
              </span>
              <ArrowUpRight
                className="w-10 h-10 md:w-16 md:h-16 shrink-0 text-primary-foreground/60 transition-transform duration-500 group-hover:translate-x-2 group-hover:-translate-y-2"
              />
            </span>
          </Link>
        </Reveal>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-12 py-16 md:py-20">
          <div className="col-span-2 md:col-span-1">
            <img
              src="/assets/vm-logo-white.png"
              alt="Varun Marbles"
              data-testid="footer-logo"
              className="w-full max-w-[220px] md:max-w-none h-auto mb-6"
            />
            <p className="text-sm font-light text-primary-foreground/60 leading-relaxed">
              Quartz slabs, marble, granite and onyx — curated with obsession since our founding.
            </p>
          </div>
          <div>
            <p className="text-xs tracking-[0.25em] uppercase text-primary-foreground/50 mb-5">Collections</p>
            <ul className="space-y-3">
              {STONES.slice(0, 5).map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/collections/${s.slug}`}
                    data-testid={`footer-stone-${s.slug}`}
                    className="text-sm font-light text-primary-foreground/75 hover:text-primary-foreground transition-colors duration-300"
                  >
                    {s.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-xs tracking-[0.25em] uppercase text-primary-foreground/50 mb-5">Company</p>
            <ul className="space-y-3">
              <li><Link to="/about" data-testid="footer-link-about" className="text-sm font-light text-primary-foreground/75 hover:text-primary-foreground transition-colors duration-300">About</Link></li>
              <li><Link to="/collections" data-testid="footer-link-collections" className="text-sm font-light text-primary-foreground/75 hover:text-primary-foreground transition-colors duration-300">Collections</Link></li>
              <li><Link to="/blog" data-testid="footer-link-blog" className="text-sm font-light text-primary-foreground/75 hover:text-primary-foreground transition-colors duration-300">Blog</Link></li>
              <li><Link to="/contact" data-testid="footer-link-contact" className="text-sm font-light text-primary-foreground/75 hover:text-primary-foreground transition-colors duration-300">Contact</Link></li>
            </ul>
          </div>
          <div>
            <p className="text-xs tracking-[0.25em] uppercase text-primary-foreground/50 mb-5">Visit us</p>
            <ul className="space-y-3 text-sm font-light text-primary-foreground/75">
              <li>
                <a href="tel:+919773558193" data-testid="footer-phone" className="flex items-start gap-2.5 hover:text-primary-foreground transition-colors duration-300">
                  <Phone className="w-4 h-4 mt-0.5 shrink-0" /> +91 97735 58193
                </a>
              </li>
              <li>
                <a href="mailto:enquiries@varunmarbles.in" data-testid="footer-email" className="flex items-start gap-2.5 hover:text-primary-foreground transition-colors duration-300">
                  <Mail className="w-4 h-4 mt-0.5 shrink-0" /> enquiries@varunmarbles.in
                </a>
              </li>
            </ul>
            <div className="mt-6 flex gap-3" data-testid="footer-socials">
              {SOCIALS.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className="w-10 h-10 border border-primary-foreground/20 flex items-center justify-center text-primary-foreground/70 transition-colors duration-300 hover:bg-primary-foreground hover:text-primary hover:border-primary-foreground"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t border-primary-foreground/15 pt-12 pb-14" data-testid="footer-showrooms">
          <p className="text-xs tracking-[0.25em] uppercase text-primary-foreground/50 mb-8">Our showrooms</p>
          <div className="grid sm:grid-cols-2 gap-8 md:gap-12">
            {SHOWROOMS.map((s) => (
              <a
                key={s.id}
                href={s.link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`footer-showroom-${s.id}`}
                className="group flex items-start gap-3"
              >
                <MapPin className="w-4 h-4 mt-1 shrink-0 text-[#E23B4E]" />
                <span>
                  <span className="font-serif text-xl font-light block mb-1 transition-colors duration-300 group-hover:text-primary-foreground">
                    {s.label} — {s.name}
                  </span>
                  <span className="text-sm font-light text-primary-foreground/60 leading-relaxed block">
                    {s.address}
                  </span>
                </span>
              </a>
            ))}
          </div>
        </div>

        <div className="border-t border-primary-foreground/15 pt-8 flex flex-col md:flex-row justify-between gap-3 text-xs text-primary-foreground/40 tracking-wide">
          <p>© {new Date().getFullYear()} Varun Marbles. All rights reserved.</p>
          <p>Stone that outlives trends.</p>
        </div>
      </div>
    </footer>
  );
}
